/**
 * The canonical skeleton builder (RIG mode).
 *
 * Turns a `CanonicalRigPlacement` (18 joint world positions) into a real three.js `Skeleton`
 * following plan D4: every bone has **identity local rotation** in rest, so placement is expressed
 * purely as local position offsets `joint - parentJoint`. Also estimates a starting placement from
 * a model's bounds and turns an unrigged `Mesh` + `SkinData` into a bound `SkinnedMesh`.
 *
 * Pure domain code: three.js math and object graph only, no DOM, no editor state.
 */

import {
  Bone,
  Float32BufferAttribute,
  Matrix4,
  Mesh,
  Skeleton,
  SkinnedMesh,
  Uint16BufferAttribute,
} from 'three';
import type { Box3, Material } from 'three';

import {
  AppError,
  CANONICAL_BONE_IDS,
  CANONICAL_CHILDREN,
  CANONICAL_PARENT,
  DEFAULT_JOINT_POSITIONS,
  RIG_VERSION,
  identityQuat,
} from '../shared';
import type {
  CanonicalBoneId,
  CanonicalRigPlacement,
  QuaternionTuple,
  RigBinding,
  SkinData,
  Vec3Tuple,
} from '../shared';
import { computeRestAlignment } from './restAlignment';

/** Height of the reference T-pose in `DEFAULT_JOINT_POSITIONS`, in metres. */
export const REFERENCE_RIG_HEIGHT = 1.8;

export interface BuiltCanonicalSkeleton {
  /** The `Root` bone; every other canonical bone is a descendant. */
  root: Bone;
  bones: Record<CanonicalBoneId, Bone>;
  /** Bones in `CANONICAL_BONE_IDS` order; `boneInverses` are the rest world inverses. */
  skeleton: Skeleton;
}

// ---------------------------------------------------------------------------
// Placement helpers (pure)
// ---------------------------------------------------------------------------

function cloneJointPositions(
  positions: Record<CanonicalBoneId, Vec3Tuple>,
): Record<CanonicalBoneId, Vec3Tuple> {
  const out = {} as Record<CanonicalBoneId, Vec3Tuple>;
  for (const id of CANONICAL_BONE_IDS) {
    const p = positions[id];
    out[id] = [p[0], p[1], p[2]];
  }
  return out;
}

/**
 * A starting joint placement for an unrigged model: the reference 1.8 m T-pose scaled uniformly
 * to the model's height, dropped so its ground plane (the `Root` joint, y = 0 in the reference)
 * sits on `bounds.min.y`, and centred on the bounds in x/z.
 *
 * Only a useful starting point — the user corrects it in RIG mode (SPEC "Initial skeleton
 * placement").
 */
export function estimatePlacement(bounds: Box3): CanonicalRigPlacement {
  const height = bounds.isEmpty() ? 0 : bounds.max.y - bounds.min.y;
  const scale = height > 1e-6 ? height / REFERENCE_RIG_HEIGHT : 1;

  // Lowest joint of the reference rig (the Root, at y = 0) becomes the model's floor.
  let lowest = Infinity;
  for (const id of CANONICAL_BONE_IDS) lowest = Math.min(lowest, DEFAULT_JOINT_POSITIONS[id][1]);

  const centerX = bounds.isEmpty() ? 0 : (bounds.min.x + bounds.max.x) / 2;
  const centerZ = bounds.isEmpty() ? 0 : (bounds.min.z + bounds.max.z) / 2;
  const minY = bounds.isEmpty() ? 0 : bounds.min.y;

  const offsetX = centerX - scale * DEFAULT_JOINT_POSITIONS.Root[0];
  const offsetY = minY - scale * lowest;
  const offsetZ = centerZ - scale * DEFAULT_JOINT_POSITIONS.Root[2];

  const jointWorldPositions = {} as Record<CanonicalBoneId, Vec3Tuple>;
  for (const id of CANONICAL_BONE_IDS) {
    const p = DEFAULT_JOINT_POSITIONS[id];
    jointWorldPositions[id] = [
      p[0] * scale + offsetX,
      p[1] * scale + offsetY,
      p[2] * scale + offsetZ,
    ];
  }

  return { rigVersion: RIG_VERSION, jointWorldPositions };
}

const LEFT_TO_RIGHT: ReadonlyArray<readonly [CanonicalBoneId, CanonicalBoneId]> = [
  ['LeftUpperArm', 'RightUpperArm'],
  ['LeftLowerArm', 'RightLowerArm'],
  ['LeftHand', 'RightHand'],
  ['LeftUpperLeg', 'RightUpperLeg'],
  ['LeftLowerLeg', 'RightLowerLeg'],
  ['LeftFoot', 'RightFoot'],
];

/**
 * Copy one side's joints onto the other, mirrored across x = 0 (canonical models face +Z with the
 * mirror plane at x = 0). `from` names the side that is kept. Centre bones are untouched.
 */
export function mirrorPlacement(
  placement: CanonicalRigPlacement,
  from: 'left' | 'right',
): CanonicalRigPlacement {
  const positions = cloneJointPositions(placement.jointWorldPositions);

  for (const [left, right] of LEFT_TO_RIGHT) {
    const source = from === 'left' ? positions[left] : positions[right];
    const targetId = from === 'left' ? right : left;
    positions[targetId] = [-source[0], source[1], source[2]];
  }

  return { rigVersion: placement.rigVersion, jointWorldPositions: positions };
}

/**
 * Move one joint by `delta`. By default the joint's whole subtree moves with it, which preserves
 * every child bone's length — the behaviour a joint-placement gizmo needs. Pass
 * `{ moveChildren: false }` to move the single joint and let the two adjacent bones stretch.
 */
export function translateJoint(
  placement: CanonicalRigPlacement,
  id: CanonicalBoneId,
  delta: Vec3Tuple,
  options: { moveChildren?: boolean } = {},
): CanonicalRigPlacement {
  const moveChildren = options.moveChildren ?? true;
  const positions = cloneJointPositions(placement.jointWorldPositions);

  const affected: CanonicalBoneId[] = [id];
  if (moveChildren) {
    for (let i = 0; i < affected.length; i++) {
      for (const child of CANONICAL_CHILDREN[affected[i]]) affected.push(child);
    }
  }

  for (const bone of affected) {
    const p = positions[bone];
    positions[bone] = [p[0] + delta[0], p[1] + delta[1], p[2] + delta[2]];
  }

  return { rigVersion: placement.rigVersion, jointWorldPositions: positions };
}

/** Local rest offset of a bone inside a placement: own joint minus parent joint (plan D4). */
export function localOffset(
  placement: CanonicalRigPlacement,
  id: CanonicalBoneId,
): Vec3Tuple {
  const own = placement.jointWorldPositions[id];
  const parent = CANONICAL_PARENT[id];
  if (!parent) return [own[0], own[1], own[2]];
  const p = placement.jointWorldPositions[parent];
  return [own[0] - p[0], own[1] - p[1], own[2] - p[2]];
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

/**
 * Build the canonical skeleton for a placement. Bone names are the canonical ids, local rotations
 * are identity and `skeleton.bones` follows `CANONICAL_BONE_IDS`, so
 * `skinIndex === CANONICAL_BONE_IDS.indexOf(id)` everywhere in the app.
 *
 * `Skeleton` is constructed after `root.updateMatrixWorld(true)`, so `boneInverses` are the
 * inverses of the rest world matrices.
 */
export function buildCanonicalSkeleton(placement: CanonicalRigPlacement): BuiltCanonicalSkeleton {
  const positions = placement.jointWorldPositions;
  const bones = {} as Record<CanonicalBoneId, Bone>;

  for (const id of CANONICAL_BONE_IDS) {
    const bone = new Bone();
    bone.name = id;
    bone.quaternion.identity();
    bone.scale.set(1, 1, 1);
    bones[id] = bone;
  }

  for (const id of CANONICAL_BONE_IDS) {
    const own = positions[id];
    const parentId = CANONICAL_PARENT[id];
    if (parentId) {
      const parent = positions[parentId];
      bones[id].position.set(own[0] - parent[0], own[1] - parent[1], own[2] - parent[2]);
      bones[parentId].add(bones[id]);
    } else {
      bones[id].position.set(own[0], own[1], own[2]);
    }
  }

  const root = bones.Root;
  root.updateMatrixWorld(true);

  const ordered = CANONICAL_BONE_IDS.map((id) => bones[id]);
  const skeleton = new Skeleton(ordered);

  return { root, bones, skeleton };
}

// ---------------------------------------------------------------------------
// Skinning
// ---------------------------------------------------------------------------

/**
 * Turn an unrigged mesh into a `SkinnedMesh` bound to a freshly built canonical skeleton.
 *
 * The mesh is left at identity and bound with an identity `bindMatrix`. The caller must add the
 * returned mesh AND `built.root` under one shared identity `Group`: `GLTFLoader` re-binds with an
 * identity bind matrix on reload (three issue #13897), so any transform between the mesh node and
 * the bones would survive export but not import.
 *
 * `skinData.skinIndex` entries index `CANONICAL_BONE_IDS`.
 */
export function createSkinnedMesh(
  mesh: Mesh,
  built: BuiltCanonicalSkeleton,
  skinData: SkinData,
): SkinnedMesh {
  const geometry = mesh.geometry.clone();
  const position = geometry.getAttribute('position');
  const vertexCount = position ? position.count : 0;

  if (skinData.vertexCount !== vertexCount) {
    throw new AppError(
      'SKIN_DATA_MISMATCH',
      `Skin data was generated for ${skinData.vertexCount} vertices but mesh ` +
        `"${mesh.name || '(unnamed)'}" has ${vertexCount}. Re-generate the skin weights.`,
      { meshName: mesh.name, expected: vertexCount, actual: skinData.vertexCount },
    );
  }
  if (skinData.skinIndex.length !== vertexCount * 4 || skinData.skinWeight.length !== vertexCount * 4) {
    throw new AppError(
      'SKIN_DATA_MISMATCH',
      `Skin data for mesh "${mesh.name || '(unnamed)'}" is not 4 influences per vertex.`,
      {
        meshName: mesh.name,
        skinIndexLength: skinData.skinIndex.length,
        skinWeightLength: skinData.skinWeight.length,
        vertexCount,
      },
    );
  }

  geometry.setAttribute('skinIndex', new Uint16BufferAttribute(Array.from(skinData.skinIndex), 4));
  geometry.setAttribute(
    'skinWeight',
    new Float32BufferAttribute(Array.from(skinData.skinWeight), 4),
  );

  const skinnedMesh = new SkinnedMesh(geometry, mesh.material as Material | Material[]);
  skinnedMesh.name = mesh.name || 'SkinnedBody';
  skinnedMesh.position.set(0, 0, 0);
  skinnedMesh.quaternion.identity();
  skinnedMesh.scale.set(1, 1, 1);
  skinnedMesh.updateMatrix();
  skinnedMesh.bind(built.skeleton, new Matrix4());

  return skinnedMesh;
}

// ---------------------------------------------------------------------------
// Binding
// ---------------------------------------------------------------------------

/**
 * The binding for a model that physically uses the canonical skeleton: every canonical bone maps
 * to a source bone of the same name, all rest offsets and the world correction are identity, so
 * `canonicalToSourceLocal` is the identity map (plan D5).
 *
 * It carries **no rest alignment**, which is only correct when the skeleton actually sits in the
 * canonical straight T-pose. A skeleton placed onto a real model does not — an A-pose character
 * gets an A-pose placement — so production code binds with `bindingForPlacement` instead.
 */
export function identityBinding(): RigBinding {
  const canonicalToSourceBone: Partial<Record<CanonicalBoneId, string>> = {};
  const restRotationOffsets: Partial<Record<CanonicalBoneId, QuaternionTuple>> = {};

  for (const id of CANONICAL_BONE_IDS) {
    canonicalToSourceBone[id] = id;
    restRotationOffsets[id] = identityQuat();
  }

  return {
    rigVersion: RIG_VERSION,
    canonicalToSourceBone,
    restRotationOffsets,
    worldCorrection: identityQuat(),
  };
}

/**
 * Bind-pose world position of every canonical joint of a built skeleton.
 *
 * Read from `skeleton.boneInverses` rather than the live `matrixWorld`, so it is the rest pose
 * even while the skeleton is temporarily posed (D14) — `boneMatricesForPose` does exactly that.
 */
export function builtRestJointPositions(
  built: BuiltCanonicalSkeleton,
): Record<CanonicalBoneId, Vec3Tuple> {
  const out = {} as Record<CanonicalBoneId, Vec3Tuple>;
  const world = new Matrix4();
  for (let i = 0; i < CANONICAL_BONE_IDS.length; i++) {
    const id = CANONICAL_BONE_IDS[i];
    const inverse = built.skeleton.boneInverses[i];
    if (!inverse) {
      out[id] = [0, 0, 0];
      continue;
    }
    world.copy(inverse).invert();
    const e = world.elements;
    out[id] = [e[12], e[13], e[14]];
  }
  return out;
}

/**
 * The binding for a tool-built skeleton that has been **placed onto a real model**.
 *
 * Identical to `identityBinding()` plus the rest alignment measured from where the user actually
 * put the joints. Without it an A-pose placement would read canonical clips as if it were a
 * T-pose, which is the 237 mm elbow error `rig/restAlignment.ts` exists to remove.
 */
export function bindingForPlacement(
  jointWorldPositions: Record<CanonicalBoneId, Vec3Tuple>,
): RigBinding {
  return {
    ...identityBinding(),
    restAlignment: computeRestAlignment(jointWorldPositions).alignment,
  };
}
